/**
 * ew-lens-selector — the native "Select Lenses" flow.
 *
 * Replaces Easify Product Options, which priced the same flow with an app-side surcharge
 * that never became a real line item and shipped ~997 KB of inline config on every page of
 * the store. Here every PRICED choice is a variant of a real product added as its own cart
 * line; every free choice rides along as a line-item property on the frame. Both are things
 * Shopify understands natively, so they appear correctly in the cart, the order and the
 * fulfilment queue.
 *
 * The markup for the steps is built on first open, not shipped in the document.
 */
(() => {
  if (customElements.get('ew-lens-selector')) return;

  const money = (cents, fmt) => {
    const n = (cents / 100).toFixed(2);
    return fmt ? fmt.replace('{{amount}}', n).replace('{{ amount }}', n) : `$${n}`;
  };

  const el = (tag, attrs = {}, ...kids) => {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v === undefined || v === null || v === false) continue;
      if (k === 'class') node.className = v;
      else if (k === 'text') node.textContent = v;
      else node.setAttribute(k, v === true ? '' : String(v));
    }
    kids.flat().forEach((c) => c && node.appendChild(c));
    return node;
  };

  const FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  class EwLensSelector extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.config = JSON.parse(this.querySelector('[data-lens-config]').textContent);
      this.dialog = this.querySelector('dialog');
      this.body = this.dialog.querySelector('[data-lens-body]');
      this.totalEl = this.dialog.querySelector('[data-lens-total]');
      this.footNote = this.dialog.querySelector('[data-lens-note]');
      this.backBtn = this.dialog.querySelector('[data-lens-back]');
      this.nextBtn = this.dialog.querySelector('[data-lens-next]');
      this.stepLabel = this.dialog.querySelector('[data-lens-step]');

      this.answers = {};
      this.built = false;

      this.querySelector('[data-lens-open]').addEventListener('click', () => this.open());
      this.dialog.querySelector('[data-lens-close]').addEventListener('click', () => this.close());
      this.backBtn.addEventListener('click', () => this.go(-1));
      this.nextBtn.addEventListener('click', () => this.next());

      // Backdrop click. A <dialog> click lands on the dialog itself only outside its box.
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('keydown', (e) => this.trapFocus(e));
    }

    /* ---------------------------------------------------------------- open/close */

    open() {
      this.trigger = document.activeElement;
      // Resolved per open, not per page load — see currentVariant().
      this.variant = this.currentVariant();
      if (!this.built) {
        this.build();
        this.built = true;
      }
      this.dialog.showModal();
      document.documentElement.style.setProperty('overflow', 'hidden');
      this.render();
    }

    close() {
      this.dialog.close();
      document.documentElement.style.removeProperty('overflow');
      if (this.trigger && this.trigger.focus) this.trigger.focus();
    }

    trapFocus(e) {
      if (e.key !== 'Tab') return;
      const items = [...this.dialog.querySelectorAll(FOCUSABLE)].filter(
        (n) => n.offsetParent !== null
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* --------------------------------------------------------------------- steps */

    // A step applies when its `when` clause matches the answers so far. `when` is a map of
    // step id -> array of accepted labels; an empty/absent `when` always applies.
    applies(step) {
      if (!step.when) return true;
      return Object.entries(step.when).every(([id, accepted]) => {
        const a = this.answers[id];
        return a && accepted.includes(a.label);
      });
    }

    get steps() {
      return this.config.steps.filter((s) => this.applies(s));
    }

    build() {
      this.index = 0;
    }

    render() {
      const steps = this.steps;
      if (this.index >= steps.length) this.index = steps.length - 1;
      const step = steps[this.index];

      this.body.textContent = '';
      this.body.appendChild(this.renderStep(step));

      this.stepLabel.textContent = `Step ${this.index + 1} of ${steps.length}`;
      this.backBtn.hidden = this.index === 0;
      const last = this.index === steps.length - 1;
      this.nextBtn.textContent = last ? this.config.strings.add_to_cart : this.config.strings.next;
      this.nextBtn.disabled = false;
      this.updateTotal();
      const focusable = this.body.querySelector(FOCUSABLE);
      if (focusable) focusable.focus();
    }

    renderStep(step) {
      const wrap = el('div', { class: 'ew-lens__step' });
      wrap.appendChild(el('h3', { class: 'ew-lens__title', text: step.title }));
      if (step.help) wrap.appendChild(el('p', { class: 'ew-lens__help', text: step.help }));

      if (step.type === 'prescription') {
        wrap.appendChild(this.renderPrescription(step));
        return wrap;
      }
      if (step.type === 'upload') {
        wrap.appendChild(this.renderUpload(step));
        return wrap;
      }

      const list = el('div', {
        class: `ew-lens__choices ew-lens__choices--${step.type}`,
        role: 'radiogroup',
        'aria-label': step.title,
      });
      step.choices.forEach((choice) => {
        const chosen = this.answers[step.id] && this.answers[step.id].label === choice.label;
        const btn = el(
          'button',
          {
            type: 'button',
            class: 'ew-lens__choice',
            role: 'radio',
            'aria-checked': chosen ? 'true' : 'false',
            'data-selected': chosen ? '' : undefined,
          },
          choice.image
            ? el('img', {
                class: 'ew-lens__swatch',
                src: choice.image,
                alt: '',
                width: 64,
                height: 64,
                loading: 'lazy',
              })
            : null,
          el(
            'span',
            { class: 'ew-lens__choice-text' },
            el('span', { class: 'ew-lens__choice-label', text: choice.label }),
            choice.note ? el('span', { class: 'ew-lens__choice-note', text: choice.note }) : null
          ),
          el('span', {
            class: 'ew-lens__price',
            text: choice.price ? `+ ${money(choice.price, this.config.money_format)}` : this.config.strings.included,
          })
        );
        btn.addEventListener('click', () => {
          this.answers[step.id] = choice;
          // Answers downstream of this one may no longer apply — drop them.
          const order = this.config.steps.map((s) => s.id);
          const cut = order.indexOf(step.id);
          order.slice(cut + 1).forEach((id) => delete this.answers[id]);
          this.render();
        });
        list.appendChild(btn);
      });
      wrap.appendChild(list);
      return wrap;
    }

    // Prescription values are described as a range, not shipped as a list — the four
    // dropdowns Easify inlined came to ~250 KB of markup on their own.
    range(spec) {
      const out = [''];
      const dp = spec.step < 1 ? 2 : 0;
      const signed = spec.min < 0 || spec.signed;
      for (let v = spec.min; v <= spec.max + 1e-9; v += spec.step) {
        const n = Number(v.toFixed(4));
        out.push(signed && n > 0 ? `+${n.toFixed(dp)}` : n.toFixed(dp));
      }
      return out;
    }

    renderPrescription(step) {
      const saved = this.answers[step.id] || { fields: {} };
      const grid = el('div', { class: 'ew-lens__rx' });
      const mk = (name, label, spec) => {
        const values = this.range(spec);
        const id = `ew-rx-${name}`;
        const sel = el('select', { class: 'ew-lens__select', id, name });
        values.forEach((v) =>
          sel.appendChild(el('option', { value: v, text: v, selected: saved.fields[name] === v }))
        );
        sel.addEventListener('change', () => this.captureRx(step));
        return el('div', { class: 'ew-lens__field' }, el('label', { for: id, text: label }), sel);
      };
      const eyes = [
        ['od', 'Right eye (OD)'],
        ['os', 'Left eye (OS)'],
      ];
      eyes.forEach(([key, label]) => {
        const row = el('fieldset', { class: 'ew-lens__eye' }, el('legend', { text: label }));
        row.appendChild(mk(`${key}_sph`, 'SPH', this.config.rx.sph));
        row.appendChild(mk(`${key}_cyl`, 'CYL', this.config.rx.cyl));
        row.appendChild(mk(`${key}_axis`, 'Axis', this.config.rx.axis));
        if (step.with_add) row.appendChild(mk(`${key}_add`, 'Add', this.config.rx.add));
        grid.appendChild(row);
      });
      grid.appendChild(mk('pd', 'PD (pupillary distance)', this.config.rx.pd));
      return grid;
    }

    captureRx(step) {
      const fields = {};
      this.body.querySelectorAll('select[name]').forEach((s) => {
        if (s.value) fields[s.name] = s.value;
      });
      this.answers[step.id] = { label: 'Entered', fields, price: 0 };
    }

    // The File object is kept on the answer, not on the input. A step is re-rendered every
    // time it is shown, and a fresh <input type="file"> comes back empty — so a customer who
    // uploaded, pressed Back to check a choice, then came forward again used to arrive at
    // submit with an answer naming a file that no longer existed anywhere.
    renderUpload(step) {
      const id = 'ew-rx-file';
      const saved = this.answers[step.id];
      const input = el('input', {
        type: 'file',
        id,
        name: `properties[${step.property || step.title}]`,
        accept: 'image/*,application/pdf',
        class: 'ew-lens__file',
        // aria-required, not required: this input is in no form, so `required` would only
        // paint :invalid without ever gating anything. next() is what enforces it.
        'aria-required': step.required ? 'true' : undefined,
      });
      const chosen = el('p', {
        class: 'ew-lens__file-name',
        text: saved && saved.file ? saved.file.name : '',
      });
      input.addEventListener('change', () => {
        const file = input.files && input.files[0];
        if (file) this.answers[step.id] = { label: file.name, price: 0, file };
        else delete this.answers[step.id];
        chosen.textContent = file ? file.name : '';
        this.updateTotal();
      });
      return el(
        'div',
        { class: 'ew-lens__upload' },
        el('label', { for: id, class: 'ew-lens__file-label', text: step.help || 'Upload your prescription' }),
        input,
        chosen
      );
    }

    /* --------------------------------------------------------------------- total */

    lineTotal() {
      return Object.values(this.answers).reduce((n, a) => n + (a.price || 0), 0);
    }

    updateTotal() {
      const base =
        this.variant && typeof this.variant.price === 'number'
          ? this.variant.price
          : this.config.base_price;
      const total = base + this.lineTotal();
      this.totalEl.textContent = money(total, this.config.money_format);
      const picked = Object.values(this.answers)
        .filter((a) => a.label && a.label !== 'Entered')
        .map((a) => a.label);
      this.footNote.textContent = picked.join(' · ');
    }

    /* ------------------------------------------------------------------- advance */

    go(delta) {
      this.index = Math.max(0, this.index + delta);
      this.render();
    }

    /**
     * Whether a required step is still unanswered.
     *
     * The upload step needs its own test: choosing "Upload a Photo" and then continuing
     * with nothing attached leaves the order carrying a prescription the merchant cannot
     * fulfil — the same lost prescription as a file that never reached Shopify, wearing a
     * different costume. An answer object alone is not enough; it has to hold a File.
     */
    unanswered(step) {
      if (!step.required) return false;
      const a = this.answers[step.id];
      if (!a) return true;
      return step.type === 'upload' && !(a.file instanceof File);
    }

    next() {
      const steps = this.steps;
      const step = steps[this.index];
      if (this.unanswered(step)) {
        this.footNote.textContent =
          (step.type === 'upload' && this.config.strings.pick_file) ||
          this.config.strings.pick_one;
        return;
      }
      if (this.index < steps.length - 1) {
        this.go(1);
        return;
      }
      this.submit();
    }

    /* ---------------------------------------------------------------------- cart */

    /**
     * The chosen prescription file and the property name its cart line should carry.
     * Null when the customer took one of the other two prescription paths.
     */
    upload() {
      const step = this.steps.find((s) => {
        const a = this.answers[s.id];
        return a && a.file instanceof File;
      });
      return step ? { key: step.property || step.title, file: this.answers[step.id].file } : null;
    }

    properties() {
      const props = {};
      this.steps.forEach((s) => {
        const a = this.answers[s.id];
        if (!a) return;
        // The file rides under this same property key, as the file itself — see addFrame().
        // Writing the local filename here too would compete with it for the value, and the
        // filename is precisely the useless string this defect used to ship.
        if (a.file) return;
        if (a.fields) {
          Object.entries(a.fields).forEach(([k, v]) => {
            props[this.config.rx_labels[k] || k] = v;
          });
        } else if (a.label) {
          props[s.property || s.title] = a.label;
        }
      });
      return props;
    }

    /**
     * Add the frame line.
     *
     * With a prescription attached this has to be a `multipart/form-data` post — JSON
     * cannot carry a file — so the body is built here as a FormData and sent to the very
     * same `/cart/add.js`, which accepts multipart happily and answers with the line it
     * created.
     *
     * There is deliberately **no `<form>`** in this path. The old code looked one up
     * (`form[action*='/cart/add']`) and posted through it, but this PDP has no product form
     * at all — by design there is no Add to cart in the buy box, and the only forms on the
     * page are the swatch picker and predictive search. The lookup therefore always
     * returned null, the multipart branch never ran, and execution fell through to the JSON
     * add, which silently dropped the file while `properties()` wrote the local *filename*
     * as the value. The customer saw success; the order named a prescription file that had
     * never been uploaded, and the merchant could not fulfil it.
     *
     * A form was never the requirement. Shopify stores the file and rewrites the property
     * to a CDN URL for any correctly-formed multipart post, so the DOM dependency is gone
     * rather than satisfied with a hidden form.
     */
    async addFrame(variant, props, upload) {
      const url = `${this.config.routes.cart_add}.js`;

      if (!upload) {
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variant.id), quantity: 1, properties: props }],
          }),
        });
        if (!r.ok) throw new Error(`frame ${r.status}`);
        return;
      }

      const fd = new FormData();
      fd.append('id', String(variant.id));
      fd.append('quantity', '1');
      Object.entries(props).forEach(([k, v]) => fd.append(`properties[${k}]`, v));
      fd.append(`properties[${upload.key}]`, upload.file, upload.file.name);

      // No Content-Type header on purpose — the browser has to set the multipart boundary,
      // and naming the type by hand omits it and makes Shopify reject the body.
      const r = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd,
      });
      if (!r.ok) throw new Error(`frame ${r.status}`);

      // Shopify returns the created line. A stored file comes back as a CDN URL; anything
      // else means the upload did not happen — and sending the customer to checkout
      // believing it did is this whole defect. Fail loudly instead of quietly.
      const line = await r.json().catch(() => null);
      const stored = (line && line.properties && line.properties[upload.key]) || '';
      if (!/^(https?:)?\/\//.test(String(stored))) {
        throw new Error(`prescription not stored: ${String(stored).slice(0, 60)}`);
      }
    }

    async submit() {
      this.nextBtn.disabled = true;
      this.nextBtn.textContent = this.config.strings.adding;

      const group = `L${Date.now().toString(36)}${Math.floor(Math.random() * 1e4)}`;
      const addOns = this.steps
        .map((s) => this.answers[s.id])
        .filter((a) => a && a.variant_id && a.price > 0)
        .map((a) => ({
          id: a.variant_id,
          quantity: 1,
          properties: { _lens_group: group, _for: this.config.product_title },
        }));

      const frameProps = { ...this.properties(), _lens_group: group };
      // Re-read rather than trusting the config baked at render time. F-037.
      const frameVariant = this.currentVariant();

      try {
        if (addOns.length) {
          const r = await fetch(`${this.config.routes.cart_add}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: addOns }),
          });
          if (!r.ok) throw new Error(`add-ons ${r.status}`);
        }

        await this.addFrame(frameVariant, frameProps, this.upload());

        this.close();
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        window.location.href = this.config.routes.cart;
      } catch (err) {
        this.nextBtn.disabled = false;
        this.nextBtn.textContent = this.config.strings.add_to_cart;
        this.footNote.textContent = this.config.strings.error;
        console.error('[ew-lens-selector]', err);
      }
    }

    /**
     * The frame variant to buy, read off the page at the moment it is needed.
     *
     * F-037. `this.config` is baked into a <script type="application/json"> at render time.
     * When the shopper changes colour, Horizon re-fetches the section and morphs it in — but
     * it preserves this custom element and does not rewrite that script, so the config keeps
     * whichever variant the page first loaded with. Selecting Brown and buying through the
     * popup added Pink to the cart. There is no Add to cart in this buy box by design, so
     * there was no product form to fall back to either.
     *
     * The variant picker's own JSON *is* refreshed by that morph, so read it instead, and
     * take the price from the same place so the popup's total is the price actually charged.
     *
     * Order: picker JSON, then the checked radio, then a cart form, then the baked config.
     * Everything is scoped to this element's own section on purpose — a document-wide
     * `form[action*='/cart/add']` lookup would happily return a quick-add form belonging to
     * a different product further down the page.
     *
     * A single-variant product renders no picker at all, so it falls straight through to the
     * config — which is already correct for it.
     */
    currentVariant() {
      const root = this.closest('[id^="shopify-section-"]') || document;
      const fallback = { id: this.config.variant_id, price: this.config.base_price };

      const picker = root.querySelector('variant-picker');
      if (picker) {
        const json = picker.querySelector('script[type="application/json"]');
        if (json) {
          try {
            const v = JSON.parse(json.textContent);
            if (v && v.id) {
              return { id: v.id, price: typeof v.price === 'number' ? v.price : fallback.price };
            }
          } catch (err) {
            console.warn('[ew-lens-selector] variant JSON unreadable', err);
          }
        }
        const checked = picker.querySelector('input[type="radio"][data-variant-id]:checked');
        if (checked && checked.dataset.variantId) {
          return { id: Number(checked.dataset.variantId), price: fallback.price };
        }
      }

      const input = root.querySelector("form[action*='/cart/add'] [name='id']");
      if (input && input.value) return { id: Number(input.value), price: fallback.price };

      return fallback;
    }
  }

  customElements.define('ew-lens-selector', EwLensSelector);
})();
